import { type QueryClient, queryOptions, useMutation } from '@tanstack/react-query'
import type { CreatePermission, CreateRole, InviteUser } from '@headline/schemas'
import { api } from './api'

/**
 * Roles and permissions live in WorkOS, not our database, so these lists are a view of a remote
 * system that the WorkOS dashboard can also change. staleTime stays at the 30s default — the
 * page is the primary editor and every mutation below invalidates, so drift only comes from
 * out-of-band dashboard edits, which a navigation-triggered refetch picks up soon enough.
 */
export const permissionsQuery = queryOptions({
  queryKey: ['rbac', 'permissions'],
  queryFn: async () => {
    const res = await api.api.rbac.permissions.$get()
    if (!res.ok) {
      const body = await res.json()
      throw new Error('error' in body ? body.error.message : 'Failed to load permissions')
    }
    return res.json()
  },
})

export const rolesQuery = queryOptions({
  queryKey: ['rbac', 'roles'],
  queryFn: async () => {
    const res = await api.api.rbac.roles.$get()
    if (!res.ok) {
      const body = await res.json()
      throw new Error('error' in body ? body.error.message : 'Failed to load roles')
    }
    return res.json()
  },
})

/**
 * The org-wide roles this session may hand out — the Users tab's role pickers. Not `rolesQuery`:
 * that is the role editor's catalogue behind `manage-all:users`, which a people manager need
 * not hold. The server bounds this list to roles inside what
 * the caller holds, and refuses anything outside it on write too.
 */
export const assignableRolesQuery = queryOptions({
  queryKey: ['rbac', 'assignable-roles'],
  queryFn: async () => {
    const res = await api.api.rbac['assignable-roles'].$get()
    if (!res.ok) {
      const body = await res.json()
      throw new Error('error' in body ? body.error.message : 'Failed to load roles')
    }
    return res.json()
  },
})

/** One invalidation for the family: roles embed permission slugs, so they stale together. */
function invalidateRbac(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: ['rbac'] })
}

export const membersQuery = queryOptions({
  queryKey: ['rbac', 'members'],
  queryFn: async () => {
    const res = await api.api.rbac.members.$get()
    if (!res.ok) {
      const body = await res.json()
      throw new Error('error' in body ? body.error.message : 'Failed to load members')
    }
    return res.json()
  },
})

export const invitationsQuery = queryOptions({
  queryKey: ['rbac', 'invitations'],
  queryFn: async () => {
    const res = await api.api.rbac.invitations.$get()
    if (!res.ok) {
      const body = await res.json()
      throw new Error('error' in body ? body.error.message : 'Failed to load invitations')
    }
    return res.json()
  },
})

/**
 * Everyone who can reach anything, across every account.
 *
 * Its own query rather than a slice of the org-wide overview: that one costs a WorkOS listing per
 * account and per sub-account, and this is the estate-wide question, answered from the local
 * assignment mirror in a single read.
 */
export const peopleDirectoryQuery = queryOptions({
  queryKey: ['people-directory'],
  queryFn: async () => {
    const res = await api.api.rbac.people.$get()
    if (!res.ok) throw new Error('Failed to load the people directory')
    return res.json()
  },
  staleTime: 30 * 1000,
})

export function useInviteUser(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: InviteUser) => {
      const res = await api.api.rbac.invitations.$post({ json: input })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to send invitation')
      }
      return res.json()
    },
    onSuccess: () => invalidateRbac(queryClient),
  })
}

export function useRevokeInvitation(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.api.rbac.invitations[':id'].$delete({ param: { id } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to revoke invitation')
      }
      return res.json()
    },
    onSuccess: () => invalidateRbac(queryClient),
  })
}

export function useUpdateMemberRole(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { membershipId: string; roleSlug: string }) => {
      const res = await api.api.rbac.members[':membershipId'].role.$put({
        param: { membershipId: input.membershipId },
        json: { roleSlug: input.roleSlug },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to change role')
      }
      return res.json()
    },
    onSuccess: () => invalidateRbac(queryClient),
  })
}

export function useRemoveMember(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (membershipId: string) => {
      const res = await api.api.rbac.members[':membershipId'].$delete({ param: { membershipId } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to remove member')
      }
      return res.json()
    },
    onSuccess: () => invalidateRbac(queryClient),
  })
}

/**
 * What the CALLER may do on one resource — the union of every role they hold on it, including
 * ones inherited from an ancestor and from groups, which is why it's a server round trip rather
 * than anything derivable from the session.
 *
 * This is what lets a screen gate itself on resource-scoped access instead of an org-wide claim:
 * a customer's own admin holds `account:manage-access` on their account and nothing in the JWT
 * says so. Self-service, so it reports only the caller's own access and can't probe anyone else's.
 */
export function myResourcePermissionsQuery(resourceType: string, resourceId: string) {
  return queryOptions({
    queryKey: ['me', 'permissions', resourceType, resourceId],
    queryFn: async () => {
      const res = await api.api.me['my-permissions'].$get({ query: { resourceType, resourceId } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to load your access')
      }
      return res.json()
    },
  })
}

/**
 * Ends a time-boxed grant early — the same operation its scheduled expiry performs, so there is
 * no separate "cancel the timer". Creating one is part of applying access
 * ({@link useApplyAccess} with `expiresAt`), not its own verb; the grants in force arrive with
 * the access overview rather than through a query of their own.
 */
export function useRevokeGrant(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.api.rbac.grants[':id'].$delete({ param: { id } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to revoke grant')
      }
      return res.json()
    },
    onSuccess: () => invalidateRbac(queryClient),
  })
}

export function useCreatePermission(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: CreatePermission) => {
      const res = await api.api.rbac.permissions.$post({ json: input })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to create permission')
      }
      return res.json()
    },
    onSuccess: () => invalidateRbac(queryClient),
  })
}

export function useDeletePermission(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (slug: string) => {
      const res = await api.api.rbac.permissions[':slug'].$delete({ param: { slug } })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to delete permission')
      }
      return res.json()
    },
    onSuccess: () => invalidateRbac(queryClient),
  })
}

export function useCreateRole(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: CreateRole) => {
      const res = await api.api.rbac.roles.$post({ json: input })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to create role')
      }
      return res.json()
    },
    // Also refetch on partial failure (thrown above): the role may exist without its
    // permissions, and the list showing it is what makes the error message actionable.
    onSettled: () => invalidateRbac(queryClient),
  })
}

export function useSetRolePermissions(queryClient: QueryClient) {
  return useMutation({
    mutationFn: async (input: { slug: string; permissions: string[] }) => {
      const res = await api.api.rbac.roles[':slug'].permissions.$put({
        param: { slug: input.slug },
        json: { permissions: input.permissions },
      })
      if (!res.ok) {
        const body = await res.json()
        throw new Error('error' in body ? body.error.message : 'Failed to update permissions')
      }
      return res.json()
    },
    onSuccess: () => invalidateRbac(queryClient),
  })
}
